import { nowUtcDateTime, toDateTime } from "@/lib/backtest-run";
import { buildClosedTrades } from "@/lib/backtest-stats";
import { parseScriptRef } from "@/lib/script-ref";
import type { Backtest } from "@/api/types";
import { marketTypeOf, type MarketTradeType } from "@/lib/market-provider";

export type BacktestUiMode = "lab" | "live";

export function backtestMode(bt?: { metadata?: Backtest["metadata"] } | null): BacktestUiMode {
  const mode = bt?.metadata?.ui?.mode;
  return mode === "live" ? "live" : "lab";
}

export function isLiveInstance(bt?: { metadata?: Backtest["metadata"] } | null): boolean {
  return backtestMode(bt) === "live";
}

export function isLabInstance(bt?: { metadata?: Backtest["metadata"] } | null): boolean {
  return backtestMode(bt) === "lab";
}

export function liveStats(backtests: { metadata?: Backtest["metadata"]; status?: string }[]) {
  const live = backtests.filter(isLiveInstance);
  return {
    liveTotal: live.length,
    liveRunning: live.filter((bt) => bt.status === "running").length,
  };
}

export function scriptLabel(
  bt: {
    strategy_config?: { source?: string };
    strategy_provider?: string;
    metadata?: Backtest["metadata"];
  },
  scripts?: { id: string; name?: string }[]
): string {
  const source = String(bt.strategy_config?.source || "");
  const ref = parseScriptRef(source);
  const scriptId =
    ref?.id ||
    (typeof bt.metadata?.ui?.script_id === "string" ? bt.metadata.ui.script_id : "");
  const linkedName = scriptId
    ? scripts?.find((s) => s.id === scriptId)?.name?.trim()
    : "";
  if (linkedName) return linkedName;

  const fromStrategy = source.match(/strategy\s*\(\s*"([^"]+)"/)?.[1]?.trim();
  if (fromStrategy) return fromStrategy;

  return "—";
}

/** Spot or futures, from the backtest's market config (never the symbol name). */
export function engineType(bt: { market_config?: Record<string, unknown> | null }): MarketTradeType {
  return marketTypeOf(bt.market_config);
}

/** "ETHUSDT perp · 15m" / "ETHUSDT spot · 1h". */
export function marketLabel(bt: { symbol?: string; timeframe?: string; market_config?: Record<string, unknown> | null }): string {
  return `${bt.symbol || ""} ${engineType(bt) === "futures" ? "perp" : "spot"} · ${bt.timeframe || ""}`;
}

export function accountName(accounts: { id: string; name?: string }[], accountId?: string): string {
  if (!accountId) return "—";
  return accounts.find((a) => a.id === accountId)?.name || accountId;
}

export function defaultStartDate(bt?: { start_date?: string | null } | null): string {
  return toDateTime(bt?.start_date) || nowUtcDateTime();
}

/** Same Net PnL as Live detail (`fiat_end − fiat_start`) when a result is present. */
export function liveNetPnl(row: {
  result?: {
    fiat_start?: number | null;
    initial_fiat?: number | null;
    fiat_end?: number | null;
    estimated_end_fiat?: number | null;
  } | null;
}): { net: number; pct: number } | null {
  const result = row?.result;
  if (!result) return null;
  const start = Number(result.fiat_start ?? result.initial_fiat);
  const end = Number(result.fiat_end ?? result.estimated_end_fiat);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  const net = end - start;
  const pct = start !== 0 ? (net / start) * 100 : 0;
  return { net, pct };
}

/** Grand total Net PnL across all live instances (all accounts). */
export function sumLiveNetPnl(backtests: { metadata?: Backtest["metadata"]; result?: Backtest["result"] }[]): {
  net: number;
  withPnl: number;
  liveTotal: number;
} {
  const live = backtests.filter(isLiveInstance);
  let net = 0;
  let withPnl = 0;
  for (const row of live) {
    const pnl = liveNetPnl(row);
    if (!pnl) continue;
    net += pnl.net;
    withPnl += 1;
  }
  return { net, withPnl, liveTotal: live.length };
}

type LiveForAccountPnl = {
  metadata?: Backtest["metadata"];
  result?: Backtest["result"];
  accounts?: { account_id?: string }[];
};

/** Sum Net PnL of live strategies linked to a marketplace account. */
export function sumLiveNetPnlForAccount(
  backtests: LiveForAccountPnl[],
  accountId: string
): { net: number; withPnl: number } | null {
  if (!accountId) return null;
  let net = 0;
  let withPnl = 0;
  for (const row of backtests) {
    if (!isLiveInstance(row)) continue;
    const linked = row.accounts?.some((a) => a.account_id === accountId);
    if (!linked) continue;
    const pnl = liveNetPnl(row);
    if (!pnl) continue;
    net += pnl.net;
    withPnl += 1;
  }
  if (withPnl === 0) return null;
  return { net, withPnl };
}

/** Open simulated positions across live instances (same “Open†” rows as Live → Trades). */
export function countLiveOpenTrades(backtests: Backtest[]): number {
  let total = 0;
  for (const bt of backtests) {
    if (!isLiveInstance(bt)) continue;
    const result = bt.result;
    if (!result?.trades?.length) continue;
    const closed = buildClosedTrades(
      result.trades,
      result.last_close || 0,
      result.last_date || new Date().toISOString(),
      result.fee_rate || 0,
      result.fiat_start ?? result.initial_fiat ?? 0,
      result.fiat_end ?? result.estimated_end_fiat
    );
    total += closed.filter((t) => t.is_open).length;
  }
  return total;
}

/** Start of today in the chosen display zone (UTC midnight or local midnight). */
export function startOfToday(mode: "utc" | "local", now = new Date()): Date {
  return mode === "utc"
    ? new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
    : new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

/**
 * Live instances that run a lab backtest. New ones record `metadata.ui.source_id`;
 * older ones are matched by the same script, symbol and timeframe, or by symbol
 * and timeframe alone when only one lab backtest uses them.
 */
export function liveInstancesFor(lab: Backtest, backtests: Backtest[]): Backtest[] {
  const scriptId = lab.metadata?.ui?.script_id;
  return backtests.filter((bt) => {
    if (!isLiveInstance(bt)) return false;
    const src = bt.metadata?.ui?.source_id;
    if (src) return src === lab.id;
    if (bt.symbol !== lab.symbol || bt.timeframe !== lab.timeframe) return false;
    if (scriptId && bt.metadata?.ui?.script_id === scriptId) return true;
    // Oldest instances copied the script: accept the market match only when
    // no other lab backtest trades the same symbol and timeframe.
    return !backtests.some(
      (other) => other.id !== lab.id && isLabInstance(other) && other.symbol === lab.symbol && other.timeframe === lab.timeframe
    );
  });
}
